import type { Seat } from '@campfire/engine';
import type { EuchreEvent, EuchreView } from '@campfire/game-euchre';
import { cardFace, cardLabel } from '../cards.ts';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { SUIT_GLYPH, SUIT_WORD, trickCenter, trickHand } from './trick-table.ts';

const teamName = (ctx: TableCtx, t: number) => `${name(ctx, t)} & ${name(ctx, t + 2)}`;

function summary(ctx: TableCtx, e: EuchreEvent): string {
  if (e.kind === 'redeal') return `Everyone passed: new deal.`;
  const makers = teamName(ctx, e.makers);
  if (e.euchred) return `Hand ${e.hand}: ${makers} were euchred (${e.tricks} tricks) — +2 to ${teamName(ctx, e.to)}.`;
  return `Hand ${e.hand}: ${makers} took ${e.tricks}${e.tricks === 5 ? (e.alone ? ' alone — a lone march' : ' — a march') : ''}: +${e.points}.`;
}

export const euchreUi: GameUi = {
  id: 'euchre',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as EuchreView;
    const tags = [s === v.dealer ? 'dealer' : '', s === v.maker ? `called ${SUIT_GLYPH[v.trump ?? ''] ?? ''}${v.alone ? ' alone' : ''}` : '', s === v.sittingOut ? 'sits out' : ''];
    return [...tags.filter(Boolean), `${v.won[s] ?? 0} tricks`].join(' · ');
  },
  render(ctx) {
    const v = ctx.msg.view as EuchreView;
    const me = v.you;
    const partner = me === null ? null : (me + 2) % 4;
    const alone = !!ctx.local.get<boolean>('euchre-alone');
    const send = (a: { type: string } & Record<string, unknown>) => {
      ctx.local.set('euchre-alone', false);
      ctx.act(alone ? { ...a, alone: true } : a);
    };
    const bidding = v.phase === 'order' || v.phase === 'call';
    const team = (t: 0 | 1) =>
      h(
        `div.trick-score.team${me !== null && me % 2 === t ? '.mine' : ''}`,
        { 'data-testid': `team-${t}` },
        h('span.pname', {}, teamName(ctx, t)),
        h('span.big-num', {}, String(v.scores[t])),
        h('span.muted', {}, v.phase === 'play' ? `${(v.won[t] ?? 0) + (v.won[t + 2] ?? 0)} tricks this hand` : ''),
      );
    const tag = (s: Seat) => [s === partner ? 'partner' : null, s === v.dealer ? 'D' : null, s === v.maker ? 'maker' : null].filter(Boolean).join(' · ') || null;
    const aloneToggle = h(
      `button${alone ? '.primary' : ''}`,
      { 'data-testid': 'euchre-alone', 'aria-pressed': alone ? 'true' : 'false', onclick: () => ctx.local.set('euchre-alone', !alone) },
      alone ? 'Going alone ✓' : 'Go alone',
    );
    const controls = (): HTMLElement | null => {
      if (!ctx.myTurn) return null;
      if (v.phase === 'order' && v.upCard) {
        const suit = SUIT_WORD[v.upCard.suit ?? ''];
        return h(
          'div.trick-controls',
          {},
          h('div.hint', { 'data-testid': 'hint' }, me === v.dealer ? `Pick up the ${cardLabel(v.upCard)} and make ${suit} trumps?` : `Order the dealer to pick up — ${suit} trumps?`),
          h(
            'div.row.actions.wrap',
            {},
            h('button.primary.big', { 'data-testid': 'euchre-order', onclick: () => send({ type: 'order' }) }, me === v.dealer ? 'Pick it up' : 'Order it up'),
            aloneToggle,
            h('button.big', { 'data-testid': 'euchre-pass', onclick: () => ctx.act({ type: 'pass' }) }, 'Pass'),
          ),
        );
      }
      if (v.phase === 'call') {
        return h(
          'div.trick-controls',
          {},
          h('div.hint', { 'data-testid': 'hint' }, v.canPass ? 'Name trumps, or pass.' : 'Stuck! The dealer must name trumps.'),
          h(
            'div.row.actions.wrap',
            {},
            v.callSuits.map((s) => h(`button.big.suit-${s}`, { 'data-testid': `euchre-call-${s}`, onclick: () => send({ type: 'call', suit: s }) }, `${SUIT_GLYPH[s]} ${SUIT_WORD[s]}`)),
          ),
          h('div.row.actions.wrap', {}, aloneToggle, v.canPass ? h('button.big', { 'data-testid': 'euchre-pass', onclick: () => ctx.act({ type: 'pass' }) }, 'Pass') : null),
        );
      }
      if (v.phase === 'discard') return h('div.hint', { 'data-testid': 'hint' }, 'Tap a card to discard it face down.');
      return h('div.hint', { 'data-testid': 'hint' }, 'Tap a highlighted card to play it.');
    };
    return h(
      'div.trick-game.euchre',
      {},
      h('div.trick-scores.teams', { 'data-testid': 'scores' }, team(0), team(1)),
      v.trump
        ? h('div.trump-banner', { 'data-testid': 'trump' }, `Trumps: ${SUIT_GLYPH[v.trump]} ${SUIT_WORD[v.trump]}`, h('span.muted', {}, ` · ${v.maker === null ? '' : name(ctx, v.maker)} called${v.alone ? ' alone' : ''}`))
        : null,
      bidding && v.upCard
        ? h(
            'div.trick-center.upcard',
            { 'data-testid': 'trick' },
            cardFace(v.upCard, { testid: 'euchre-upcard' }),
            h('div.muted', {}, v.phase === 'order' ? `Turned up · ${name(ctx, v.dealer)} deals` : 'Turned down — name another suit'),
          )
        : trickCenter(ctx, { trick: v.trick, lastTrick: v.lastTrick, seats: 4, turn: v.turn, tag, sittingOut: v.sittingOut }),
      h(
        'div.event',
        { 'data-testid': 'event' },
        v.last && (v.phase === 'order' || v.result) ? summary(ctx, v.last) : null,
        v.result ? null : ` ${v.turn === null ? '' : name(ctx, v.turn)} ${v.turn === ctx.you ? 'are' : 'is'} up.`,
      ),
      controls(),
      trickHand(ctx, v.hand, v.legal, (c) => ctx.act({ type: v.phase === 'discard' ? 'discard' : 'play', card: c.id }), { dimOthers: ctx.myTurn && (v.phase === 'play' || v.phase === 'discard') }),
      me !== null && me === v.sittingOut ? h('div.hint', {}, 'Your partner is going alone — you sit this hand out.') : null,
      h('div.muted.trick-foot', {}, `Hand ${v.handNo} · first to ${v.target}`),
    );
  },
};
